import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { OfferState, PersonBasicInsuranceState, PersonState, PersonSupplementaryInsuranceState } from './offer.state';
import { assertPresent } from '../../utils/typeUtils';
import { GenderOrPrenatal, Level } from '../../api';
import { DateService } from '../../services/date.service';

const insurancesWithMultiYearContractOption: (keyof PersonSupplementaryInsuranceState)[] = [
  'outpatient',
  'hospital',
  'complementary',
  'basic',
  'hospitalCapital',
  'hospitalDailyCash',
];

export const getOfferState = createFeatureSelector<OfferState>('offer');

export const getPersons = createSelector(getOfferState, (state) => state.persons);

export const getPersonCount = createSelector(getPersons, (persons) => persons.length);

export const hasPersons = createSelector(getPersonCount, (count) => count > 0);

export const getTotalPrice = createSelector(getOfferState, (state) => state.totalPrice);

export const getAddress = createSelector(getOfferState, (state) => assertPresent(state.address, 'state.address'));

export const getAddressOrUndefined = createSelector(getOfferState, (state) => state.address);

export const getSaleOfferBegin = createSelector(getOfferState, (state) =>
  assertPresent(state.saleOfferBegin, 'state.saleOfferBegin'),
);

export const getContractDuration = createSelector(getOfferState, (state) => state.contractDuration);

export const hasFrameworkContract = createSelector(getOfferState, (state) => !!state.frameworkContract);

export const getFrameworkContractSyriusKeyOrUndefined = createSelector(
  getOfferState,
  (state) => state.frameworkContract?.syriusKey,
);

export const getFrameworkContract = createSelector(getOfferState, (state) => state.frameworkContract);

export const getFrameworkContractNumberOrUndefined = createSelector(
  getOfferState,
  (state) => state.frameworkContract?.number,
);

export const isTupCustomer = createSelector(getOfferState, (state) => state.tupCustomer);

export const isExistingCustomer = createSelector(getOfferState, (state) => state.existingCustomer);

export interface PersonTrackingInformation {
  gender: GenderOrPrenatal;
  basicInsurance?: PersonBasicInsuranceState;
  supplementaryInsurances: PersonSupplementaryInsuranceState;
  youthPackage?: Level;
}

export const getTrackingInformation = createSelector(
  getPersons,
  getContractDuration,
  getTotalPrice,
  (persons, contractDuration, totalPrice) => ({
    persons: persons.map((person) => {
      let personTrackingInformation: PersonTrackingInformation = {
        gender: person.details.genderOrPrenatal,
        basicInsurance: person.basicInsurance,
        supplementaryInsurances: person.supplementaryInsurances,
        youthPackage: person.youthPackage,
      };
      return personTrackingInformation;
    }),
    contractDuration,
    totalPrice,
  }),
);

export const createHasBasicInsurance = (personId: number): MemoizedSelector<Object, boolean> =>
  createSelector(
    getPersons,
    (persons: PersonState[]) =>
      persons.find((person: PersonState) => person.personId === personId)?.basicInsurance !== undefined,
  );

export const createHasAnyOtherPersonBeforeWithLegalProtection = (personId: number): MemoizedSelector<Object, boolean> =>
  createSelector(getPersons, (persons: PersonState[]) =>
    persons
      .filter((person: PersonState) => person.personId !== personId && person.personId < personId)
      .some((person) => person.supplementaryInsurances.legalProtection !== undefined),
  );

export const createGetPerson = (personId: number): MemoizedSelector<Object, PersonState> =>
  createSelector(
    getPersons,
    (persons: PersonState[]): PersonState =>
      assertPresent(
        persons.find((person: PersonState) => person.personId === personId),
        'person find',
      ),
  );

export const createGetPersonOrUndefined = (personId: number): MemoizedSelector<Object, PersonState | undefined> =>
  createSelector(getPersons, (persons: PersonState[]): PersonState | undefined =>
    persons.find((person: PersonState) => person.personId === personId),
  );

export const createGetNextPersonOrUndefined = (currentId: number): MemoizedSelector<Object, PersonState | undefined> =>
  createSelector(getPersons, (persons: PersonState[]): PersonState | undefined => {
    const currentIndex = persons.findIndex((person: PersonState) => person.personId === currentId);
    return currentIndex === -1 ? undefined : persons[currentIndex + 1];
  });
export const createGetPreviousPersonOrUndefined = (
  currentId: number,
): MemoizedSelector<Object, PersonState | undefined> =>
  createSelector(getPersons, (persons: PersonState[]): PersonState | undefined => {
    const currentIndex = persons.findIndex((person: PersonState) => person.personId === currentId);
    return currentIndex === -1 ? undefined : persons[currentIndex - 1];
  });

export const getIdOfFirstPersonWithoutProducts = createSelector(
  getPersons,
  (persons: PersonState[]): number | undefined => persons.filter((person) => !personHasProducts(person))[0]?.personId,
);

export const personHasProducts = (person: PersonState): boolean => {
  const hasSupplementaryInsurance = Object.values(person.supplementaryInsurances).filter(Boolean).length > 0;
  const hasBasicInsurance = person.basicInsurance !== undefined;
  return hasBasicInsurance || hasSupplementaryInsurance;
};

export const allPersonsHaveProducts = createSelector(getPersons, (persons) => persons.every(personHasProducts));

export const someChildIsUnderOneYearOldAtOfferBegin = createSelector(getOfferState, (state) => {
  const saleOfferBegin = state.saleOfferBegin;
  if (!saleOfferBegin) {
    return false;
  }
  return state.persons.some((person) =>
    DateService.isUnderOneYearOldAtOfferBegin(person.details.dateOfBirth, saleOfferBegin),
  );
});

export const somePersonHasInsuranceWithMultiYearContractOption = createSelector(getPersons, (persons) =>
  persons.some((person) =>
    insurancesWithMultiYearContractOption.some((insurance) => person.supplementaryInsurances[insurance] !== undefined),
  ),
);
